import { useState, useEffect, useCallback } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { ReactComponent as CloseIcon } from "../images/icons/white-close.svg";
import { getApiBaseUrl, getModels } from "../api-helper";
import axios from "axios";
import eventBus from "../utils/eventBus";

// Placeholder image for cosyflows without example_urls
const PlaceholderImg =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23190A4E' width='400' height='300'/%3E%3Ctext fill='%2300D9FF' font-family='Arial' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3ENo Preview%3C/text%3E%3C/svg%3E";

function CosyflowCard({ cosyflow, onSelect, onImageClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const exampleImages = cosyflow.metadata?.example_urls || [PlaceholderImg];
  const hasExamples =
    cosyflow.metadata?.example_urls &&
    cosyflow.metadata.example_urls.length > 0;
  const hasMultipleImages = exampleImages.length > 1;

  const currentImageUrl = exampleImages[currentImageIndex];

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(
      (prev) => (prev - 1 + exampleImages.length) % exampleImages.length
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % exampleImages.length);
  };

  return (
    <div
      className="group flex flex-col overflow-hidden rounded-xl border border-white/70 bg-[#101014] text-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-white/30 hover:shadow-2xl"
      onClick={() => onSelect(cosyflow)}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <img
          src={currentImageUrl}
          alt={cosyflow.title}
          className="h-full w-full cursor-pointer object-cover transition-transform duration-300 group-hover:scale-105"
          onClick={(e) => {
            e.stopPropagation();
            hasExamples && onImageClick(cosyflow);
          }}
        />

        {/* Category badge – top left */}
        {cosyflow.category && (
          <div className="absolute left-3 top-3 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold">
            {cosyflow.category}
          </div>
        )}

        {/* Image navigation arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white opacity-0 transition-opacity group-hover:opacity-100"
              aria-label="Previous image"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white opacity-0 transition-opacity group-hover:opacity-100"
              aria-label="Next image"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Counter */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-2 py-0.5 text-xs font-semibold">
              {currentImageIndex + 1} / {exampleImages.length}
            </div>
          </>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col px-4 pb-4 pt-3">
        <h3 className="mb-1 truncate text-sm font-semibold">
          {cosyflow.title}
        </h3>
        <p className="line-clamp-2 text-xs text-white/70">
          {cosyflow.description || "AI workflow for generating content"}
        </p>

        <div className="mt-3 flex items-center justify-between text-[11px] text-white/50">
          <span>Workflow</span>
          <button
            className="rounded-md bg-white/5 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-white/10"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(cosyflow);
            }}
          >
            Use
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CosyflowsGallery({ isOpen, onClose, onSelectCosyflow }) {
  const [cosyflows, setCosyflows] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryFlow, setGalleryFlow] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);

  useEffect(() => {
    if (isOpen && cosyflows.length === 0) {
      fetchCosyflows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchCosyflows = async () => {
    try {
      setIsLoading(true);
      const apiBase = await getApiBaseUrl();

      const [modelsResponse, cosyflowsResponse] = await Promise.all([
        getModels({ host: apiBase }, () => {}),
        axios.get(`${apiBase}/api/cosyflows/query`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}`,
          },
        }),
      ]);

      const availableModelNames = modelsResponse?.models || [];

      if (
        cosyflowsResponse.data.cosyflows &&
        cosyflowsResponse.data.cosyflows.length > 0
      ) {
        const transformedFlows = cosyflowsResponse.data.cosyflows
          .map((flow) => {
            let cosyflowData = {};
            try {
              cosyflowData =
                typeof flow.cosyflow === "string"
                  ? JSON.parse(flow.cosyflow)
                  : flow.cosyflow;
            } catch (err) {
              console.error("Error parsing cosyflow:", err);
            }

            return {
              id: flow.id,
              title: cosyflowData.title || flow.title || "Untitled Workflow",
              description: cosyflowData.description || "",
              category: cosyflowData.category || "General",
              metadata: cosyflowData.metadata || {},
              cosyflowData: cosyflowData,
            };
          })
          .filter((flow) => availableModelNames.includes(flow.title));

        const uniqueCategories = [
          "All",
          ...new Set(transformedFlows.map((f) => f.category).filter(Boolean)),
        ];

        setCosyflows(transformedFlows);
        setCategories(uniqueCategories);
        setFetchError(null);
      } else {
        setFetchError("No cosyflows available");
      }
    } catch (error) {
      console.error("Failed to fetch cosyflows or models:", error);
      if (error.message.includes("getModels")) {
        setFetchError(
          "Failed to load available models. Please check your connection."
        );
      } else {
        setFetchError("Failed to load workflows. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCosyflows = cosyflows.filter((flow) => {
    const matchesCategory =
      selectedCategory === "All" || flow.category === selectedCategory;

    const q = searchQuery.toLowerCase();
    const matchesSearch =
      q === "" ||
      flow.title.toLowerCase().includes(q) ||
      (flow.description && flow.description.toLowerCase().includes(q)) ||
      (flow.category && flow.category.toLowerCase().includes(q));

    return matchesCategory && matchesSearch;
  });

  const handleCosyflowSelect = (cosyflow) => {
    eventBus.emit("selectModel", {
      modelName: cosyflow.title,
      openSettings: true,
    });

    if (onSelectCosyflow) {
      onSelectCosyflow(cosyflow);
    }

    onClose();
  };

  const handleImageClick = useCallback((cosyflow) => {
    setGalleryFlow(cosyflow);
    setCurrentGalleryImageIndex(0);

    const exampleUrls = cosyflow.metadata?.example_urls || [];
    const images = exampleUrls.map((url, index) => ({
      url,
      description: `Example ${index + 1}`,
      model: cosyflow.title,
    }));

    setGalleryImages(images);
    setIsGalleryOpen(true);
  }, []);

  if (!isOpen) return null;

  const categoryList = categories.filter((c) => c !== "All");

  return (
    <>
      {/* Main Gallery Modal */}
      <Modal
        open={isOpen}
        onClose={onClose}
        center
        container={document.querySelector('.main-content') || undefined}
        closeIcon={<CloseIcon className="fill-white" />}
        classNames={{
          modal: "customModal",
          overlay: "customOverlay",
        }}
        styles={{
          modal: {
            background: "#190A4E",
            borderRadius: "18px",
            padding: "1.75rem",
            maxWidth: "1200px",
            width: "95%",
            maxHeight: "90vh",
            overflow: "hidden",
            zIndex: 99999999,
          },
          overlay: {
            background: "rgba(0, 0, 0, 0.85)",
            zIndex: 99999998,
          },
          closeButton: {
            width: "24px",
            height: "24px",
            top: "12px",
            right: "12px",
            zIndex: 99999999,
          },
        }}
      >
        <div className="flex h-[72vh] gap-6 text-white">
          {/* Sidebar */}
          <aside className="flex w-56 flex-col border-r border-white/10 pr-6 text-sm">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-white">
                Templates
              </p>
              <button
                className="mt-3 flex w-full items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-xs font-semibold"
                onClick={() => setSelectedCategory("All")}
              >
                <span>All Workflows</span>
              </button>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-white">
                Categories
              </p>
              <div className="space-y-1">
                {categoryList.map((category) => {
                  const active = selectedCategory === category;
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`flex w-full items-center justify-between rounded-md px-3 py-1.5 text-xs ${
                        active
                          ? "bg-white text-black"
                          : "text-white/70 hover:bg-white/5"
                      }`}
                    >
                      <span className="truncate">{category}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex flex-1 flex-col overflow-hidden">
            {/* Top bar: title + search/filters */}
            <div className="mb-4 flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">
                All Workflows
              </h2>
            </div>

            <div className="mb-5 flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workflows"
                  className="h-9 w-full rounded-lg border border-white/10 bg-white/5 pl-9 pr-3 text-xs text-white placeholder:text-white focus:border-white/30 focus:outline-none"
                />
              </div>

              {/* Filter buttons (visual only for now) */}
              {/* <button className="hidden whitespace-nowrap rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/80 hover:bg-white/10 sm:block">
                Model Filter
              </button>
              <button className="hidden whitespace-nowrap rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/80 hover:bg-white/10 md:block">
                Use Case
              </button>
              <button className="hidden whitespace-nowrap rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/80 hover:bg-white/10 lg:block">
                License
              </button>

              <button className="whitespace-nowrap rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/80 hover:bg-white/10">
                Newest ▾
              </button> */}
            </div>

            {/* Error / loading / grid */}
            {fetchError && !isLoading && (
              <div className="mb-4 rounded-lg border border-yellow-400/40 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-100">
                {fetchError}
              </div>
            )}

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="inline-block h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
                  <p className="mt-3 text-sm text-white/70">
                    Loading workflows...
                  </p>
                </div>
              </div>
            ) : filteredCosyflows.length > 0 ? (
              <div className="flex-1 overflow-y-auto pr-1">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredCosyflows.map((cosyflow) => (
                    <CosyflowCard
                      key={cosyflow.id}
                      cosyflow={cosyflow}
                      onSelect={handleCosyflowSelect}
                      onImageClick={handleImageClick}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <p className="text-sm text-white/60">
                  No workflows found matching your search.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All");
                  }}
                  className="mt-3 rounded-lg bg-white px-4 py-2 text-xs font-semibold text-black hover:bg-white/90"
                >
                  Clear filters
                </button>
              </div>
            )}
          </main>
        </div>
      </Modal>

      {/* Gallery Examples Modal (kept mostly as-is, minor colour tweak) */}
      <Modal
        open={isGalleryOpen}
        onClose={() => {
          setIsGalleryOpen(false);
          setGalleryFlow(null);
          setGalleryImages([]);
          setCurrentGalleryImageIndex(0);
        }}
        center
        container={document.querySelector('.main-content') || undefined}
        closeIcon={<CloseIcon className="fill-white" />}
        classNames={{
          modal: "customModal",
          overlay: "customOverlay",
        }}
        styles={{
          modal: {
            background: "#050509",
            borderRadius: "18px",
            padding: 0,
            maxWidth: "72rem",
            width: "95%",
            maxHeight: "90vh",
          },
          overlay: {
            background: "rgba(0, 0, 0, 0.9)",
          },
          closeButton: {
            width: "24px",
            height: "24px",
            top: "12px",
            right: "12px",
            zIndex: 10,
          },
        }}
      >
        {galleryFlow && galleryImages.length > 0 ? (
          <div className="p-6 text-white">
            <div className="mb-6">
              <h2 className="mb-1 text-2xl font-semibold">
                {galleryFlow.title}
              </h2>
              {galleryFlow.category && (
                <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
                  {galleryFlow.category}
                </span>
              )}
              {galleryFlow.description && (
                <p className="mt-3 text-sm text-white/70">
                  {galleryFlow.description}
                </p>
              )}
            </div>

            <div className="mb-5">
              <div className="flex gap-4 overflow-x-auto pb-3">
                {galleryImages.map((image, index) => (
                  <div
                    key={index}
                    className="group relative flex-shrink-0 rounded-lg border border-white/10 bg-black/40"
                  >
                    <img
                      src={image.url}
                      alt={image.description}
                      className="h-48 w-72 object-cover"
                    />
                    <div className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-xs">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsGalleryOpen(false);
                  setGalleryFlow(null);
                  setGalleryImages([]);
                  setCurrentGalleryImageIndex(0);
                }}
                className="flex-1 rounded-lg border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleCosyflowSelect(galleryFlow);
                  setIsGalleryOpen(false);
                  setGalleryFlow(null);
                  setGalleryImages([]);
                  setCurrentGalleryImageIndex(0);
                }}
                className="flex-1 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-black hover:bg-white/90"
              >
                Use this workflow
              </button>
            </div>
          </div>
        ) : (
          <div className="flex h-64 items-center justify-center text-white">
            <div className="text-center">
              <p className="text-sm">No example images available</p>
              <button
                onClick={() => {
                  setIsGalleryOpen(false);
                  setGalleryFlow(null);
                  setGalleryImages([]);
                  setCurrentGalleryImageIndex(0);
                }}
                className="mt-4 rounded-lg bg-white px-4 py-2 text-xs font-semibold text-black hover:bg-white/90"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
